import { useEffect, useMemo, useState } from 'react';
import {
  Stack, Group, Button, TextInput, Textarea, Select, Alert, Paper, Table, Text, Checkbox, Loader, Center,
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { notifications } from '@mantine/notifications';
import { IconAdd as IconPlus, IconCustomer } from '../../components/icons/CncIcons';
import { api, imageUrl } from '../../api/client';
import { useAuth, canWrite } from '../../store/auth';
import { folderNameError } from '../../lib/nameValidation';
import { COUNTRY_NAMES, countiesFor } from '../../lib/geo';
import OrderStatusBar, { type OrderStatus } from './OrderStatusBar';
import CustomerPickerModal from './CustomerPickerModal';
import OrderPartModal, { type OrderPart, type PendingPart } from './OrderPartModal';
import DeleteOrderSection from './DeleteOrderSection';
import type { Customer } from '../customers/CustomersPage';
import type { Technician } from '../technicians/TechniciansPage';
import type { Contact } from '../customers/ContactModal';

interface FullOrder {
  id: number;
  orderUid: string;
  customerId: number | null;
  customerName: string | null;
  contactId: number | null;
  technicianId: number | null;
  name: string;
  notes: string | null;
  imagePath: string | null;
  orderDate: string;
  state: string;
  status: OrderStatus;
  shipment: { useDefault: boolean; country: string | null; county: string | null; street: string | null; zip: string | null };
  parts: OrderPart[];
}

interface CustomerDetail extends Customer {
  contacts: Contact[];
}

export default function OrderSetupPage() {
  const { id: idParam } = useParams();
  const navigate = useNavigate();
  const { me } = useAuth();
  const writable = canWrite(me?.role);
  const isEdit = !!idParam && idParam !== 'new';
  const orderId = isEdit ? Number(idParam) : null;

  const [loading, setLoading] = useState(isEdit);
  const [order, setOrder] = useState<FullOrder | null>(null);

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [customerContacts, setCustomerContacts] = useState<Contact[]>([]);
  const [contactId, setContactId] = useState<number | null>(null);
  const [technicianId, setTechnicianId] = useState<number | null>(null);
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [previewUid, setPreviewUid] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [orderDate, setOrderDate] = useState<Date | null>(new Date());

  const [shipUseDefault, setShipUseDefault] = useState(true);
  const [shipCountry, setShipCountry] = useState<string | null>(null);
  const [shipCounty, setShipCounty] = useState<string | null>(null);
  const [shipStreet, setShipStreet] = useState('');
  const [shipZip, setShipZip] = useState('');

  const [pickingCustomer, setPickingCustomer] = useState(false);
  const [editingPart, setEditingPart] = useState<OrderPart | PendingPart | 'new' | null>(null);
  // In-memory parts buffer used while the order hasn't been saved yet.
  const [pendingParts, setPendingParts] = useState<PendingPart[]>([]);

  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Load technicians always
  useEffect(() => {
    api.get<Technician[]>('/technicians').then(setTechnicians);
  }, []);

  // Load existing order
  useEffect(() => {
    if (!isEdit || !orderId) return;
    (async () => {
      try {
        const o = await api.get<FullOrder>(`/orders/${orderId}`);
        setOrder(o);
        setName(o.name);
        setNotes(o.notes ?? '');
        setOrderDate(new Date(o.orderDate));
        setTechnicianId(o.technicianId);
        setContactId(o.contactId);
        setShipUseDefault(o.shipment.useDefault);
        setShipCountry(o.shipment.country);
        setShipCounty(o.shipment.county);
        setShipStreet(o.shipment.street ?? '');
        setShipZip(o.shipment.zip ?? '');
        if (o.customerId) {
          const c = await api.get<CustomerDetail>(`/customers/${o.customerId}`);
          setCustomer(c);
          setCustomerContacts(c.contacts ?? []);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [isEdit, orderId]);

  // When customer changes while creating, preview UID and preload contacts
  useEffect(() => {
    if (isEdit) return;
    if (!customer) { setPreviewUid(null); setCustomerContacts([]); setContactId(null); return; }
    (async () => {
      try {
        const r = await api.get<{ orderUid: string }>(`/orders/next-uid?customerId=${customer.id}`);
        setPreviewUid(r.orderUid);
      } catch { setPreviewUid(null); }
      try {
        const d = await api.get<CustomerDetail>(`/customers/${customer.id}`);
        setCustomerContacts(d.contacts ?? []);
      } catch { setCustomerContacts([]); }
    })();
  }, [customer, isEdit]);

  // Default shipment from customer when checkbox is on
  const shipFromCustomer = useMemo(() => ({
    country: customer?.address.country ?? null,
    county: customer?.address.county ?? null,
    street: customer?.address.street ?? '',
    zip: customer?.address.zip ?? '',
  }), [customer]);

  const effectiveShipment = shipUseDefault ? shipFromCustomer : { country: shipCountry, county: shipCounty, street: shipStreet, zip: shipZip };
  const shipCounties = countiesFor(shipUseDefault ? shipFromCustomer.country : shipCountry);

  async function refreshOrder() {
    if (!orderId) return;
    const o = await api.get<FullOrder>(`/orders/${orderId}`);
    setOrder(o);
  }

  function validate(): string | null {
    if (!customer) return 'Please select a customer.';
    if (!name.trim()) return 'Order name is required.';
    const nameErr = folderNameError(name.trim());
    if (nameErr) return nameErr;
    if (!orderDate) return 'Order date is required.';
    return null;
  }

  async function handleSave() {
    setErr(null);
    const problem = validate();
    if (problem) return setErr(problem);
    setBusy(true);
    try {
      const shipment = {
        useDefault: shipUseDefault,
        country: effectiveShipment.country,
        county: effectiveShipment.county,
        street: effectiveShipment.street || null,
        zip: effectiveShipment.zip || null,
      };
      if (isEdit && orderId) {
        // Advance status to In Quotation if still in Order Set-up; otherwise preserve current status.
        const advance = order?.status === 'Order Set-up' ? { status: 'In Quotation' as OrderStatus } : {};
        await api.put(`/orders/${orderId}`, {
          customerId: customer!.id,
          contactId, technicianId,
          name: name.trim(),
          notes: notes.trim() || null,
          orderDate: orderDate ? dayjs(orderDate).toISOString() : undefined,
          shipment,
          ...advance,
        });
        notifications.show({ message: 'Order saved.', color: 'green' });
        navigate(`/orders/${orderId}`);
      } else {
        const created = await api.post<FullOrder>('/orders', {
          customerId: customer!.id,
          contactId, technicianId,
          name: name.trim(),
          notes: notes.trim() || null,
          orderDate: dayjs(orderDate!).toISOString(),
          state: 'ONGOING',
          status: 'In Quotation' as OrderStatus,
          shipment,
        });
        // Flush in-memory parts against the newly-created order.
        for (const pp of pendingParts) {
          try {
            await api.post(`/orders/${created.id}/parts`, {
              name: pp.name,
              imagePath: pp.imagePath,
              quantity: pp.quantity,
              sharepointLocation: pp.sharepointLocation,
              notes: pp.notes,
            });
          } catch (ex: any) {
            notifications.show({ message: `Part "${pp.name}" failed to save: ${ex.message ?? ex}`, color: 'red' });
          }
        }
        notifications.show({ message: `Order ${created.orderUid} created.`, color: 'green' });
        navigate(`/orders/${created.id}`);
      }
    } catch (e: any) {
      setErr(e.message ?? 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  // Where to go when the user dismisses this screen. If the order is past Set-up,
  // the user arrived from the In Quotation page and should land back there.
  // Otherwise go to the orders list (for /orders/new and pristine Set-up).
  const dismissPath = isEdit && order && order.status !== 'Order Set-up' ? `/orders/${orderId}` : '/orders';
  const cameFromInQuotation = isEdit && !!order && order.status !== 'Order Set-up';

  if (loading) return <Center p="xl"><Loader /></Center>;

  const parts = order?.parts ?? [];

  return (
    <Stack>
      <OrderStatusBar current={isEdit ? (order?.status ?? 'Order Set-up') : 'Order Set-up'} />

      <div className="section">
        <div className="section-header">Basics</div>
        <div className="section-body">
          <Stack>
            <Group align="end">
              <Button
                data-tour="order-pick-customer"
                variant="light"
                leftSection={<IconCustomer size={16} />}
                onClick={() => setPickingCustomer(true)}
                disabled={!writable || isEdit}
              >
                {customer ? `Customer: ${customer.name}` : 'Select Customer'}
              </Button>
              {customer && customer.logoPath && (
                <img src={imageUrl(customer.logoPath, true)} alt={customer.name} style={{ maxHeight: 36 }} />
              )}
            </Group>

            <TextInput
              data-tour="order-uid"
              label="Order ID"
              value={(isEdit ? order?.orderUid : previewUid) ?? '— select a customer —'}
              readOnly
              disabled
            />

            <TextInput
              data-tour="order-name"
              label="Order Name"
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              maxLength={100}
              required
              disabled={!writable}
              error={name ? folderNameError(name) : null}
            />
            <Textarea
              data-tour="order-notes"
              label="Order Notes"
              value={notes}
              onChange={(e) => setNotes(e.currentTarget.value)}
              maxLength={500}
              autosize
              minRows={3}
              description={`${notes.length}/500`}
              disabled={!writable}
            />

            <Select
              data-tour="order-contact"
              label="Order Contact"
              placeholder={customer ? 'Pick a contact (optional)' : 'Select a customer first'}
              data={customerContacts.map((c) => ({ value: String(c.id), label: c.name }))}
              value={contactId ? String(contactId) : null}
              onChange={(v) => setContactId(v ? Number(v) : null)}
              clearable
              disabled={!writable || !customer}
            />
            <Select
              data-tour="order-technician"
              label="Order Technician"
              placeholder="Lab technician who leads this order"
              data={technicians.map((t) => ({ value: String(t.id), label: t.name }))}
              value={technicianId ? String(technicianId) : null}
              onChange={(v) => setTechnicianId(v ? Number(v) : null)}
              clearable
              disabled={!writable}
            />
            <DateTimePicker
              data-tour="order-date"
              label="Order Date + Time"
              value={orderDate}
              onChange={setOrderDate}
              valueFormat="YYYY-MM-DD HH:mm"
              disabled={!writable}
            />
          </Stack>
        </div>
      </div>

      <div className="section">
        <div className="section-header">Order Parts</div>
        <div className="section-body">
          <Paper withBorder>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 72 }}>Image</Table.Th>
                  <Table.Th style={{ width: 160 }}>Part UID</Table.Th>
                  <Table.Th>Name</Table.Th>
                  <Table.Th style={{ width: 120 }}>Quantity</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {isEdit ? parts.map((p) => (
                  <Table.Tr key={p.id} className="click-row" onClick={() => setEditingPart(p)}>
                    <Table.Td>
                      <div className="image-box">
                        {p.imagePath && <img src={imageUrl(p.imagePath, true)} alt={p.name} />}
                      </div>
                    </Table.Td>
                    <Table.Td><code>{p.partUid}</code></Table.Td>
                    <Table.Td>{p.name}</Table.Td>
                    <Table.Td>{p.quantity}</Table.Td>
                  </Table.Tr>
                )) : pendingParts.map((p) => (
                  <Table.Tr key={p.id} className="click-row" onClick={() => setEditingPart(p)}>
                    <Table.Td>
                      <div className="image-box">
                        {p.imagePath && <img src={imageUrl(p.imagePath, true)} alt={p.name} />}
                      </div>
                    </Table.Td>
                    <Table.Td><Text size="xs" c="dimmed">(assigned on save)</Text></Table.Td>
                    <Table.Td>{p.name}</Table.Td>
                    <Table.Td>{p.quantity}</Table.Td>
                  </Table.Tr>
                ))}
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Button data-tour="order-add-part" fullWidth variant="light" leftSection={<IconPlus size={14} />} onClick={() => setEditingPart('new')} disabled={!writable}>
                      Add Part
                    </Button>
                  </Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
          </Paper>
          {!isEdit && pendingParts.length > 0 && (
            <Text size="xs" c="dimmed" mt="xs">
              Parts are held in memory and will be saved when you create the order. Leaving this page without saving discards them.
            </Text>
          )}
        </div>
      </div>

      <div className="section">
        <div className="section-header">Shipment Details</div>
        <div className="section-body">
          <Stack>
            <Checkbox
              label="Use customer's default address"
              checked={shipUseDefault}
              onChange={(e) => setShipUseDefault(e.currentTarget.checked)}
              disabled={!writable || !customer}
            />
            <Select
              label="Country"
              data={COUNTRY_NAMES}
              searchable
              clearable
              value={shipUseDefault ? shipFromCustomer.country : shipCountry}
              onChange={setShipCountry}
              disabled={!writable || shipUseDefault}
            />
            {shipCounties ? (
              <Select
                label="County"
                data={shipCounties}
                searchable
                clearable
                value={shipUseDefault ? shipFromCustomer.county : shipCounty}
                onChange={setShipCounty}
                disabled={!writable || shipUseDefault}
              />
            ) : (
              <TextInput
                label="County"
                value={shipUseDefault ? (shipFromCustomer.county ?? '') : (shipCounty ?? '')}
                onChange={(e) => setShipCounty(e.currentTarget.value || null)}
                disabled={!writable || shipUseDefault}
              />
            )}
            <TextInput
              label="Street Address"
              value={shipUseDefault ? shipFromCustomer.street : shipStreet}
              onChange={(e) => setShipStreet(e.currentTarget.value)}
              disabled={!writable || shipUseDefault}
            />
            <TextInput
              label="ZIP Code"
              value={shipUseDefault ? shipFromCustomer.zip : shipZip}
              onChange={(e) => setShipZip(e.currentTarget.value)}
              disabled={!writable || shipUseDefault}
            />
          </Stack>
        </div>
      </div>

      {err && <Alert color="red">{err}</Alert>}

      <Group grow mt="md">
        <Button variant="default" onClick={() => navigate(dismissPath)}>Cancel</Button>
        {cameFromInQuotation && <Button variant="default" onClick={() => navigate(dismissPath)}>Close</Button>}
        {writable && <Button data-tour="order-save" onClick={handleSave} loading={busy} size="md">Save</Button>}
      </Group>

      {isEdit && orderId && <DeleteOrderSection orderId={orderId} />}

      {pickingCustomer && (
        <CustomerPickerModal
          onPick={(c) => { setCustomer(c); setPickingCustomer(false); }}
          onClose={() => setPickingCustomer(false)}
        />
      )}
      {editingPart && (
        <OrderPartModal
          orderId={orderId ?? undefined}
          initial={editingPart === 'new' ? null : editingPart}
          onClose={() => setEditingPart(null)}
          onDone={async () => { if (isEdit) await refreshOrder(); setEditingPart(null); }}
          onLocalSave={!isEdit ? (body) => {
            setPendingParts((prev) => {
              if (editingPart !== 'new' && editingPart && 'id' in (editingPart as any) && (editingPart as any).id < 0) {
                // editing an in-memory pending part
                return prev.map((p) => p.id === (editingPart as PendingPart).id ? { ...p, ...body } : p);
              }
              return [...prev, { ...body, id: -1 - prev.length }];
            });
            setEditingPart(null);
          } : undefined}
          onLocalDelete={!isEdit && editingPart !== 'new' ? () => {
            const target = editingPart as PendingPart;
            setPendingParts((prev) => prev.filter((p) => p.id !== target.id));
            setEditingPart(null);
          } : undefined}
        />
      )}
    </Stack>
  );
}
